import { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontal scroll carousel with arrow navigation.
 * Replaces autonomous marquee/scroll with user-driven navigation.
 *
 * Props:
 *  - children: cards to render horizontally
 *  - itemWidthClass: tailwind width class per item (e.g. "w-72 sm:w-80")
 *  - testId: root data-testid (arrows get -prev / -next suffix)
 *  - scrollStep: pixels per arrow click (default: viewport-based)
 */
export default function HScroll({ children, itemWidthClass = "w-72 sm:w-80", testId = "hscroll", scrollStep }) {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    update();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [children]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const step = scrollStep || Math.round(el.clientWidth * 0.85);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const items = Array.isArray(children) ? children : [children];

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => scroll(-1)}
        disabled={!canPrev}
        aria-label="Précédent"
        data-testid={`${testId}-prev`}
        className={`hidden sm:flex absolute -left-3 lg:-left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-primary/15 shadow-md items-center justify-center transition-all ${canPrev ? "opacity-100 hover:bg-primary hover:text-white hover:border-primary" : "opacity-40 cursor-not-allowed"}`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={() => scroll(1)}
        disabled={!canNext}
        aria-label="Suivant"
        data-testid={`${testId}-next`}
        className={`hidden sm:flex absolute -right-3 lg:-right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-primary/15 shadow-md items-center justify-center transition-all ${canNext ? "opacity-100 hover:bg-primary hover:text-white hover:border-primary" : "opacity-40 cursor-not-allowed"}`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      <div
        ref={trackRef}
        className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 hscroll-hide-scrollbar"
      >
        {items.map((child, i) => (
          <div key={i} className={`flex-shrink-0 ${itemWidthClass} snap-start`}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}
